import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useStore } from '@/lib/store';
import api from '@/lib/api';
import { Firm } from '@/types/settings';
import { Badge } from '@/components/ui/badge';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Code,
  FileText,
  Globe,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { PeppolAPI } from '@/services/peppol';
import {
  PeppolDocument,
  PeppolDocumentStatus,
  getPeppolStatusColor,
  getPeppolStatusLabel,
} from '@/types/peppol';

interface PeppolDocumentsProps {
  firmId?: string | number;
}

export function PeppolDocuments({ firmId }: PeppolDocumentsProps) {
  const { clients, fetchClients } = useStore();
  const [firm, setFirm] = useState<Firm | null>(null);
  const [documents, setDocuments] = useState<PeppolDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'outgoing' | 'incoming'>('all');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<PeppolDocumentStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // XML preview
  const [selectedDocument, setSelectedDocument] = useState<PeppolDocument | null>(null);
  const [xmlContent, setXmlContent] = useState<string | null>(null);
  const [loadingXml, setLoadingXml] = useState(false);
  const [isXmlDialogOpen, setIsXmlDialogOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Fetch firm info to get the firm's VAT number and name
  useEffect(() => {
    const fetchFirmInfo = async () => {
      if (!firmId) return;
      try {
        const response = await api.get(`/api/firms/${firmId}/`);
        setFirm(response.data);
      } catch (error) {
        console.error('Failed to fetch firm info:', error);
      }
    };
    fetchFirmInfo();
  }, [firmId]);

  // Create a map of VAT numbers to client names for quick lookup
  const vatToClientMap = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((client) => {
      if (client.vat_number) {
        // Store with normalized VAT number (remove spaces, uppercase)
        const normalizedVat = client.vat_number.replace(/\s/g, '').toUpperCase();
        map.set(normalizedVat, client.name);
      }
    });
    return map;
  }, [clients]);

  // Normalize VAT number for comparison (remove spaces, country prefix, uppercase)
  const normalizeVatNumber = useCallback((vat: string): string => {
    // Remove spaces and convert to uppercase
    let normalized = vat.replace(/\s/g, '').toUpperCase();
    // Remove common country prefixes for comparison
    normalized = normalized.replace(/^(BE|NL|DE|FR|LU|AT|IT|ES|PT|PL|CZ|SK|HU|RO|BG|HR|SI|LT|LV|EE|FI|SE|DK|IE|EL|CY|MT)/, '');
    return normalized;
  }, []);

  // Extract VAT number from Peppol ID (format: scheme:id, e.g., "0208:0891516805")
  const getParticipantName = useCallback((peppolId: string | null | undefined): string => {
    if (!peppolId) return '-';
    
    // Extract the ID part after the colon
    const parts = peppolId.split(':');
    const vatNumber = parts.length > 1 ? parts[1] : peppolId;
    
    // Normalize the VAT number for lookup
    const normalizedVat = normalizeVatNumber(vatNumber);
    
    // First, check if this matches the firm's VAT number
    if (firm?.vat_number) {
      const firmVatNormalized = normalizeVatNumber(firm.vat_number);
      if (normalizedVat === firmVatNormalized) {
        return firm.name;
      }
    }
    
    // Try to find a matching client by normalized VAT
    for (const [clientVat, clientName] of vatToClientMap.entries()) {
      if (normalizeVatNumber(clientVat) === normalizedVat) {
        return clientName;
      }
    }
    
    return peppolId;
  }, [vatToClientMap, firm, normalizeVatNumber]);

  useEffect(() => {
    // Fetch clients if not already loaded
    if (clients.length === 0) {
      fetchClients();
    }
  }, [clients.length, fetchClients]);

  useEffect(() => {
    fetchDocuments();
  }, [activeTab, statusFilter, page]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const filters: any = {
        limit: pageSize,
        offset: page * pageSize,
      };
      
      if (activeTab !== 'all') {
        filters.direction = activeTab;
      }
      
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      
      const response = await PeppolAPI.getDocuments(filters);
      setDocuments(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Failed to fetch Peppol documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Peppol documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await PeppolAPI.syncDocuments({ direction: 'all', days: 7 });
      toast({
        title: 'Sync Complete',
        description: `Synced ${result.synced} documents. New: ${result.new_documents}, Updated: ${result.updated_documents}`,
      });
      fetchDocuments();
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: 'Failed to sync documents with Peppol network',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleViewXml = async (document: PeppolDocument) => {
    setSelectedDocument(document);
    setLoadingXml(true);
    setIsXmlDialogOpen(true);
    try {
      const result = await PeppolAPI.getDocumentXml(document.id);
      setXmlContent(result.xml_content);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load document XML',
        variant: 'destructive',
      });
      setXmlContent(null);
    } finally {
      setLoadingXml(false);
    }
  };

  const handleCopyXml = () => {
    if (xmlContent) {
      navigator.clipboard.writeText(xmlContent);
      toast({
        title: 'Copied',
        description: 'XML copied to clipboard',
      });
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.sender_id.toLowerCase().includes(query) ||
      doc.recipient_id.toLowerCase().includes(query) ||
      doc.netfly_document_id?.toLowerCase().includes(query)
    );
  });

  const statusOptions: { value: PeppolDocumentStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'sending', label: 'Sending' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'failed', label: 'Failed' },
    { value: 'received', label: 'Received' },
    { value: 'processed', label: 'Processed' },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <CardTitle>Peppol Documents</CardTitle>
            </div>
            <Button variant="outline" onClick={handleSync} disabled={syncing}>
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync with Network
            </Button>
          </div>
          <CardDescription>
            View and manage documents sent and received via the Peppol network
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <SearchInput
              placeholder="Search by participant ID..."
              value={searchQuery}
              onChange={setSearchQuery}
              className="flex-1 min-w-[200px]"
            />
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as any)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
            <TabsList>
              <TabsTrigger value="all">All Documents</TabsTrigger>
              <TabsTrigger value="outgoing">
                <ArrowUpRight className="h-4 w-4 mr-1" />
                Outgoing
              </TabsTrigger>
              <TabsTrigger value="incoming">
                <ArrowDownLeft className="h-4 w-4 mr-1" />
                Incoming
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No documents found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sender</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="text-sm">
                          <span title={doc.sender || doc.sender_id}>
                            {getParticipantName(doc.sender || doc.sender_id)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span title={doc.recipient || doc.recipient_id}>
                            {getParticipantName(doc.recipient || doc.recipient_id)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={getPeppolStatusColor(doc.status)}>
                            {getPeppolStatusLabel(doc.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {doc.sent_at
                            ? format(parseISO(doc.sent_at), 'dd/MM/yyyy HH:mm')
                            : doc.created_at
                            ? format(parseISO(doc.created_at), 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewXml(doc)}
                            title="View XML"
                          >
                            <Code className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Pagination */}
              {totalCount > pageSize && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * pageSize + 1} to{' '}
                    {Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={(page + 1) * pageSize >= totalCount}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* XML Preview Dialog */}
      <Dialog open={isXmlDialogOpen} onOpenChange={setIsXmlDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Document XML</DialogTitle>
            <DialogDescription>
              {selectedDocument && (
                <>
                  {selectedDocument.direction === 'outgoing' ? 'Sent to' : 'Received from'}{' '}
                  <span className="font-mono">
                    {selectedDocument.direction === 'outgoing'
                      ? selectedDocument.recipient_id
                      : selectedDocument.sender_id}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] border rounded-md">
            {loadingXml ? (
              <div className="flex justify-center items-center h-full">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : xmlContent ? (
              <pre className="p-4 text-xs font-mono whitespace-pre-wrap">{xmlContent}</pre>
            ) : (
              <div className="flex justify-center items-center h-full text-muted-foreground">
                Failed to load XML content
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={handleCopyXml} disabled={!xmlContent}>
              Copy to Clipboard
            </Button>
            <Button onClick={() => setIsXmlDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
